<template>
  <div class="contenedor-principal">
    <h1>Día Mundial de la Tierra: Historia y cómo celebrarlo</h1>
    
    <h2>El Origen: La semilla de la conciencia ambiental</h2>
    <p>En el año 1968, el Servicio de Salud Pública de Estados Unidos organizó una conferencia sobre medio ambiente llamada <strong>Simposio de Ecología Humana</strong>, que se enfocaba en los efectos del deterioro de la <strong>biodiversidad</strong> en la salud humana. El evento fue un éxito y esto llevó a que el profesor Morton Hilbert y ciertos grupos de estudiantes desarrollaran la idea de un día dedicado a nuestro planeta.</p>
    
    <p>Finalmente, el 22 de abril de 1970, el senador y activista estadounidense <strong>Gaylord Nelson</strong> promovió una manifestación a favor del medio ambiente que contó con la participación de más de 2.000 universidades, 10.000 escuelas y cientos de comunidades diferentes. Esta presión social sin precedentes llevó al Gobierno de Estados Unidos a crear la <strong>Agencia de Protección Ambiental (EPA)</strong> y a aprobar una serie de leyes vitales para la defensa del medio ambiente.</p>
    
    <h2>¿Por qué el 22 de abril?</h2>
    <p>Desde entonces, el 22 de abril se celebra cada año en todo el mundo como el <strong>Día Mundial de la Tierra</strong>. Aunque se llevaba celebrando décadas antes, no fue hasta 2009 que las <strong>Naciones Unidas</strong> lo declararon una efeméride oficial marcada en las agendas de todos los países.</p>
    
    <p>El senador Nelson eligió esta fecha de forma estratégica para asegurarse la mayor asistencia posible de profesores y estudiantes, y así difundir el mensaje a gran escala. Se aseguró de que no coincidiera con exámenes, vacaciones de primavera ni festividades religiosas. Aunque algunos críticos intentaron boicotear la fecha alegando que coincidía con el nacimiento de Lenin (tildándolo de "engaño comunista"), la realidad es que la elección fue puramente pragmática y centrada en promover la <strong>conciencia ambiental</strong>.</p>
    
    <h2>1990: El año en que el mundo se unió</h2>
    <p>Desde que se estableció la fecha, más países se han unido cada año. Pero hubo un hito que destacó: el 22 de abril de 1990, en el vigésimo aniversario. En todo el mundo se celebraron acciones masivas:</p>
    <ul>
      <li><strong>Francia:</strong> Una multitud formó una cadena humana de casi 800 km a lo largo del río Loira.</li>
      <li><strong>Italia:</strong> 5.000 personas bloquearon autovías para protestar contra la contaminación de los vehículos.</li>
      <li><strong>Jordania y Japón:</strong> Estudiantes y ambientalistas organizaron jornadas de limpieza masiva y promovieron grandes plantas de reciclaje.</li>
    </ul>
    
    <h2>Invertir en nuestro planeta</h2>
    <p>Recientemente, el <strong>Día de la Tierra</strong> se ha celebrado bajo lemas muy potentes como <strong>“Invertir en nuestro planeta”</strong>. Las Naciones Unidas recuerdan que la defensa del medio ambiente no es solo tarea de gobiernos y empresas; todos debemos involucrarnos.</p>
    
    <p>Para fomentar la inclusión global, se anima a cada persona a cuidar su entorno cercano: desde formar grupos para plantar árboles o limpiar playas, hasta educar a otros. Puedes compartir tus acciones usando los hashtags <strong>#DíaInternacionaldelaMadreTierra</strong> y <strong>#DíadelaTierra</strong>.</p>
    
    <p>Otra forma de participar es apoyando el proyecto <strong>"Campeones de la Tierra"</strong> de la ONU, que premia a personas y organizaciones que inspiran con su trabajo, destacando la innovación, la ciencia y el liderazgo ecológico.</p>
    
    <div class="caja-recurso">
      <h3>Celebra el Día de la Tierra con Geobizi</h3>
      <p>
        La mejor manera de honrar nuestro planeta es aprendiendo a cuidarlo. Te animamos a participar en nuestras actividades medioambientales con tu familia, colegio o grupo de amigos. Apoyando proyectos locales de educación ambiental como Geobizi, ayudas a impulsar la sostenibilidad en tu entorno durante todo el año.
      </p>
<div class="botones-recurso">
  <router-link to="/calendario" class="proximas-actividades">Ver Calendario de Actividades</router-link>
  <router-link to="/servicios" class="btn-ver-mas">Descubre nuestros Servicios</router-link>
</div>
    </div>

    <p class="social-media"><b>¡Síguenos en redes sociales para ver cómo celebramos este y otros días importantes para el medio ambiente!</b></p>
    <div class="redes-sociales">
      <a href="https://www.instagram.com/geotxiki/" target="_blank" rel="noopener noreferrer" title="Geobizi en Instagram">
        <img src="/imagenes/iconos/instagram.png" alt="Geobizi en Instagram" class="social-icon" loading="lazy" />
      </a>
      <a href="https://www.facebook.com/geobizirik/" target="_blank" rel="noopener noreferrer" title="Geobizi en Facebook">
        <img src="/imagenes/iconos/facebook.png" alt="Geobizi en Facebook" class="social-icon" loading="lazy" />
      </a>
      <a href="https://www.tiktok.com/@geobizi" target="_blank" rel="noopener noreferrer" title="Geobizi en TikTok">
        <img src="/imagenes/iconos/tiktok.png" alt="Geobizi en TikTok" class="social-icon" loading="lazy" />
      </a>
    </div>

    <div class="imagen-trio">
      <div>
        <img
          class="img"
          src="/imagenes/blog/detalle/diatierra1.avif"
          alt="Grupo de personas colaborando en una plantación comunitaria en el Día de la Tierra"
          title="Día de la Tierra — plantación comunitaria"
          loading="lazy"
        />
      </div>
      <div>
        <img
          class="img"
          src="/imagenes/blog/detalle/diatierra2.avif"
          alt="Voluntariado ambiental realizando labores de limpieza y conservación"
          title="Voluntariado ambiental — limpieza"
          loading="lazy"
        />
      </div>
      <div>
        <img
          class="img"
          src="/imagenes/blog/detalle/diatierra3.avif"
          alt="Actividad de educación ambiental enseñando a niños sobre botánica y plantación"
          title="Día de la Tierra — educación en familia"
          loading="lazy"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { useHead } from '@vueuse/head'

// URL optimizada (coincide con el router)
const pageUrl = 'https://www.geobizi.com/blog/dia-tierra'
const ogImage = 'https://www.geobizi.com/imagenes/blog/detalle/diatierra1.avif'

useHead({
  // Título SEO
  title: 'Día Mundial de la Tierra: Historia y Actividades | Geobizi',
  meta: [
    { name: 'description', content: 'Conoce la fascinante historia del Día de la Tierra (22 de abril), el lema "Invertir en nuestro planeta" y cómo puedes celebrarlo con las actividades de Geobizi.' },
    { name: 'robots', content: 'index, follow' },
    { name: 'author', content: 'Geobizi' },
    { name: 'theme-color', content: '#0b8a4c' },
    { name: 'language', content: 'es' },
    { property: 'og:title', content: 'Día Mundial de la Tierra: Conciencia y Acción Ambiental | Geobizi' },
    { property: 'og:description', content: 'Historia, curiosidades y formas de participar en el Día Mundial de la Tierra. Únete a las actividades de Geobizi.' },
    { property: 'og:type', content: 'article' },
    { property: 'og:url', content: pageUrl },
    { property: 'og:image', content: ogImage },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:image', content: ogImage }
  ],
  link: [
    { rel: 'canonical', href: pageUrl },
    { rel: 'image_src', href: ogImage }
  ],
  script: [
    {
      type: 'application/ld+json',
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": "Día Mundial de la Tierra: Historia y cómo celebrarlo",
        "description": "Historia del Día de la Tierra, hitos importantes de concienciación y propuestas para participar en la conservación del medio ambiente.",
        "image": ogImage,
        "author": {
          "@type": "Organization",
          "name": "Geobizi",
          "url": "https://www.geobizi.com"
        },
        "publisher": {
          "@type": "Organization",
          "name": "Geobizi",
          "logo": {
            "@type": "ImageObject",
            "url": "https://www.geobizi.com/imagenes/GeobiziLogo.png"
          },
          "sameAs": [
            "https://www.facebook.com/geobizirik/",
            "https://www.instagram.com/geotxiki/",
            "https://www.tiktok.com/@geobizi",
            "https://www.youtube.com/channel/UCw-C_J0y-jKHp7Zx92lsKfg"
          ]
        },
        // Fechas requeridas para el Schema Article
        "datePublished": "2023-04-15T08:00:00+01:00",
        "dateModified": "2026-04-14T10:00:00+02:00",
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": pageUrl
        }
      })
    }
  ]
})
</script>

<style scoped>
/* CAJA DE RECURSOS */
.caja-recurso {
  background-color: var(--megashoftgreen); 
  border-left: 4px solid var(--shoftgreen); 
  padding: 1.5rem;
  margin: 2.5rem 0;
  border-radius: 0 0.5rem 0.5rem 0;
}
.caja-recurso h3 {
  margin-top: 0;
  color: var(--lightgreen);
  font-size: 1.2rem;
}
.caja-recurso p {
  margin-bottom: 1.5rem;
  color: var(--darkgrey);
}
.botones-recurso {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}
.proximas-actividades {
  background-color: var(--supershoftgreen);
  color: var(--lightgreen);
  padding: 0.6rem 1.2rem;
  text-decoration: none;
  font-weight: bold;
  border-radius: 4px;
  transition: background-color 0.3s;
}
.proximas-actividades:hover {
  background-color: var(--shoftgreen);
  color: var(--white);
}
.btn-ver-mas {
  background-color: transparent;
  color: var(--lightgreen);
  padding: 0.6rem 1.2rem;
  text-decoration: none;
  font-weight: bold;
  border: 1px solid var(--shoftgreen);
  border-radius: 4px;
  transition: all 0.3s;
}
.btn-ver-mas:hover {
  background-color: var(--shoftgreen);
  color: white;
}

/* REDES SOCIALES */
.social-media {
  margin-top: 2rem;
  color: var(--darkgrey);
}
.redes-sociales {
  margin-left: 1rem;
  display: flex;
  align-items: center;
}
.social-icon {
  width: 2.5rem; 
  margin-right: 1rem;
  margin-bottom: 1.5rem;
  transition: transform 0.3s;
}
.social-icon:hover {
  transform: scale(1.1);
}

/* LISTAS CON HOJITAS */
.contenedor-principal ul {
  list-style-type: none;
  padding-left: 0;
}
.contenedor-principal li {
  position: relative;
  padding-left: 2rem;
  margin-bottom: 0.8rem;
  line-height: 1.5;
}
.contenedor-principal li::before {
  content: '';
  position: absolute;
  left: 0;
  top: 2px;
  width: 1.2rem;
  height: 1.2rem;
  background-image: url('../../assets/Hojitas.png'); 
  background-size: contain;
  background-repeat: no-repeat;
}
</style>